<template>
    <vx-page-layout layout="sidebar">
        <template v-slot:sidebar>
            <api-destination-sidebar :apiDestinationId="props.apiDestinationId"></api-destination-sidebar>
        </template>

        <template v-if="props.apiDestinationId" v-slot:subtitle>
            <a href="/settings/integrations#/api-destinations"
                class="no-underline! text-sky-600! text-base font-normal">
                <font-awesome-icon widthAuto class="mr-1" :icon="faArrowLeft"></font-awesome-icon>
                All API Destinations
            </a>
        </template>
        <template v-if="data.friendly_name" v-slot:title>{{ data.friendly_name }}</template>
        <div>
            <h2 class="flex items-center font-extrabold text-xl mb-4">
                API Deliveries
            </h2>

            <div v-if="loading && !list.length" class="py-8 flex justify-center">
                <vx-icon-loading class="w-8 h-8" spin></vx-icon-loading>
            </div>
            <vx-box color="info" v-else-if="!list.length" class="flex flex-col px-8 py-20 gap-6 items-center text-xl text-slate-500">
                No deliveries yet.
            </vx-box>
            <vx-table v-else class="md:grid-cols-5-auto" rowBreakpoint="md">
                <vx-table-row isHeader>
                    <div>
                        Status
                    </div>
                    <div>
                        Triggered
                    </div>
                    <div>
                        Delivery ID
                    </div>
                    <div>
                        Automation
                    </div>
                </vx-table-row>

                <api-delivery-row v-for="(item, index) in list" :item="list[index]" :key="list[index].id" :automationRule="automationRuleForDelivery(list[index])"></api-delivery-row>

            </vx-table>

            <div v-if="fetchParams.cursor" class="mt-8">
                <vx-button
                    @click="loadData()"
                    color="muted-dark"
                    size="xl"
                    class="grow-0"
                    data-test="load-more-btn"
                    :loading="loading"
                >
                    Load More
                </vx-button>
            </div>
        </div>
    </vx-page-layout>
</template>

<script setup>
import { VxTable, VxTableRow, VxBox, VxButton, VxIconLoading, VxPageLayout } from '@voxie/frontend-components';
import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { customDeliveriesClient , automationRulesClient } from '~/services';
import { useApiDestinationForm } from "../form/useApiDestinationForm";
import ApiDestinationSidebar from "../ApiDestinationSidebar.vue";
import { onMounted, ref } from 'vue';
import ApiDeliveryRow from './ApiDeliveryRow.vue';
import { useToasted } from '../../../../../composables/useToasted';
import { useCursorPagination } from '../../../../../composables/useCursorPagination';

const toasted = useToasted();

const teamId = Spark.state.currentTeam.id;

const props = defineProps({
    apiDestinationId: {
        type: String,
        required: false,
        default: undefined,
    }
});

const { data } = useApiDestinationForm();
const { list, loading, fetchParams, fetchRecords } = useCursorPagination();

const defaultFilters = () => ({
    limit: 10,
    cursor: null,
    forward: false,
});

const automationActionMap = ref({});
const loadingAutomatedActions = ref(false);

const automationRuleForDelivery = (delivery) => {
    // undefined = loading
    // null = not found

    const automatedActionId = extractAutomatedActionId(delivery);

    if (!automatedActionId) {
        return null;
    }

    const automationRule = automationActionMap.value?.[String(automatedActionId)];

    if (!automationRule) {
        return loadingAutomatedActions.value ? undefined : null;
    }

    return automationRule;
}

const extractAutomatedActionId = (delivery) => {
    const deliverySourceParts = delivery?.delivery_source?.split(':');

    if (!deliverySourceParts || deliverySourceParts[0] !== 'automated_action' || !deliverySourceParts[1]) {
        return null;
    }

    return Number(deliverySourceParts[1])
}

/**
 * @param {number[]} automatedActionIds
 */
const fetchAutomatedActions = async (automatedActionIds) => {
    const idSet = new Set();

    for (const id of automatedActionIds) {
        if (!automationActionMap.value[String(id)]) {
            idSet.add(id);
        }
    }

    const idsToFetch = [...idSet];

    if (!idsToFetch.length) {
        return;
    }

    try {
        loadingAutomatedActions.value = true;
        const response = await automationRulesClient.searchActions(teamId, {
            ids: idsToFetch,
            with_trashed: 1,
        })

        for (const action of response.data.data) {
            automationActionMap.value[String(action.id)] = {
                id: action.automation_rule_id,
                name: action.automation_rule_name,
            };
        }
    } catch (error) {
        console.error(error);
        toasted.global.platform_error();
    } finally {
        loadingAutomatedActions.value = false;
    }
}

const loadData = async() => {
    const currentList = list.value;

    await fetchRecords(customDeliveriesClient.list(
        teamId,
        'api_destination',
        props.apiDestinationId,
        { ...fetchParams.value },
    ));

    const newList = list.value.filter((item) => {
        return !currentList.some((listItem) => listItem.id === item.id);
    });

    const automatedActionIds = newList
        .map((delivery) => extractAutomatedActionId(delivery))
        .filter((id) => id !== null);

    await fetchAutomatedActions(automatedActionIds);
};

onMounted(() => {
    data.value.id = props.apiDestinationId;
    fetchParams.value = defaultFilters();
    loadData()
})
</script>
